// High-intent "Xero accountants in [Town]" combo pages for the strongest local
// terms (primary towns). Each has genuinely unique, Xero-focused content so it
// targets a distinct query without cannibalising the general /accountants-[town]
// page (which the two pages cross-link). Base town data (county, nearby) is
// pulled from data/locations.js via baseSlug.
export const XERO_LOCATIONS = [
  {
    slug: "xero-accountants-tamworth",
    baseSlug: "accountants-tamworth",
    metaDescription:
      "Xero accountants in Tamworth. Xero Platinum Partner offering setup, migration, training and full cloud accounting support from our Amington office. More than numbers.",
    intro: [
      "As a Xero Platinum Partner based right here in Tamworth, we live and breathe cloud accounting. Whether you're moving off spreadsheets, switching from another system, or already on Xero and want to get more from it, we make the numbers work for your business.",
      "Being local means we can sit down with you in Amington, get your Xero set up exactly right, and train your team so you're confident using it day to day — with real-time visibility of your cash flow and profit whenever you need it.",
    ],
    faqs: [
      {
        question: "Can you migrate my Tamworth business onto Xero?",
        answer:
          "Yes. As a Xero Platinum Partner in Tamworth we handle the whole migration for you — moving your data across cleanly, reconciling opening balances and training your team so there's no disruption.",
      },
      {
        question: "Do you offer Xero training in person?",
        answer:
          "We do. Because we're based in Amington, Tamworth, we can provide hands-on Xero training in person as well as remotely, so you and your team feel genuinely confident using it.",
      },
    ],
  },
  {
    slug: "xero-accountants-sutton-coldfield",
    baseSlug: "accountants-sutton-coldfield",
    metaDescription:
      "Xero accountants for Sutton Coldfield businesses. Xero Platinum Partner offering setup, migration, training and cloud accounting support for directors and SMEs.",
    intro: [
      "Sutton Coldfield's directors, consultancies and growing SMEs increasingly want their finances in the cloud — and as a Xero Platinum Partner we help them get there. From first setup to ongoing support, we make Xero do the heavy lifting.",
      "We connect Xero to the apps that suit your business, automate the admin, and give you real-time dashboards so you always know where you stand — backed by proactive, tax-efficient advice for salary and dividend structures.",
    ],
    faqs: [
      {
        question: "I'm a Sutton Coldfield director on salary and dividends — can Xero help?",
        answer:
          "Absolutely. We use Xero to keep your Sutton Coldfield company's numbers real-time and structure your salary and dividends tax-efficiently, so there are no year-end surprises.",
      },
      {
        question: "Which Xero apps do you recommend?",
        answer:
          "It depends on your business — we recommend and set up the right add-ons (expenses, invoicing, payments, forecasting) so your Xero does more with less admin.",
      },
    ],
  },
  {
    slug: "xero-accountants-lichfield",
    baseSlug: "accountants-lichfield",
    metaDescription:
      "Xero accountants in Lichfield. Xero Platinum Partner offering setup, migration, training and unlimited cloud accounting support — just minutes from Lichfield.",
    intro: [
      "Just ten minutes from Lichfield, we're a Xero Platinum Partner helping the city's professional firms, retailers and SMEs move to genuinely modern accounting. Xero, set up properly and supported year-round — not just at year-end.",
      "We take the hassle out of the switch, train your team, and give you clear real-time numbers plus unlimited advice whenever a question comes up — all on fixed monthly fees.",
    ],
    faqs: [
      {
        question: "How quickly can you get my Lichfield business onto Xero?",
        answer:
          "Most Lichfield migrations are completed within a couple of weeks depending on complexity. We handle the data move, reconcile balances and train your team before go-live.",
      },
      {
        question: "Is Xero support included in your fees?",
        answer:
          "Yes. For Lichfield clients on fixed monthly fees, Xero support and advice are included — pick up the phone whenever you need us, with no clock running.",
      },
    ],
  },
  {
    slug: "xero-accountants-birmingham",
    baseSlug: "accountants-birmingham",
    // Designated owner of the regional "Xero accountant West Midlands" query
    // (190 impressions a year, position 7.2, previously split between the
    // homepage and the Birmingham, Solihull and West Bromwich Xero pages —
    // nothing owned it). Only this entry carries a `region`.
    region: "the West Midlands",
    metaDescription:
      "Xero accountants for Birmingham and the wider West Midlands. Boutique Xero Platinum Partner offering setup, migration, training and cloud accounting for SMEs.",
    intro: [
      "Birmingham's agencies, start-ups and established firms want finance that moves as fast as they do. As a boutique Xero Platinum Partner, we give you a dedicated contact who really knows your business, backed by leading cloud technology.",
      "We set up and streamline your Xero, automate the day-to-day, and turn your numbers into management information you can actually use to make sharper, faster decisions.",
      "Birmingham is our base for Xero work across the wider West Midlands — Solihull, Walsall, West Bromwich, Wolverhampton, Coventry and the Black Country. Wherever you are in the region, the Xero side of things is handled the same way: one named contact, everything in the cloud, and no need for anyone to drive anywhere to get it done.",
    ],
    faqs: [
      {
        question: "Do you work with Birmingham agencies and start-ups on Xero?",
        answer:
          "Yes — plenty of them. We set up Xero for growing Birmingham agencies and start-ups, connect the right apps, and provide management accounts and forecasting to support confident growth.",
      },
      {
        question: "Are you a Xero accountant for the whole of the West Midlands?",
        answer:
          "Yes. Birmingham is where most of our regional Xero work sits, and we look after Xero clients right across the West Midlands — Solihull, Walsall, West Bromwich, Wolverhampton, Coventry and the Black Country. Xero is cloud software, so the county line makes no practical difference to how we work with you.",
      },
      {
        question: "Will I get a real person or a call centre?",
        answer:
          "A real, dedicated contact who knows your Birmingham business — never a call centre. That boutique approach is what sets us apart.",
      },
    ],
  },
  {
    slug: "xero-accountants-solihull",
    baseSlug: "accountants-solihull",
    metaDescription:
      "Xero accountants for Solihull businesses near the NEC & Blythe Valley. Xero Platinum Partner offering setup, migration, training and cloud accounting support.",
    intro: [
      "From Blythe Valley to Birmingham Business Park, Solihull is full of ambitious businesses that benefit from proper cloud accounting. As a Xero Platinum Partner, we set up Xero to give you the management information and forecasting you need to grow with confidence.",
      "We migrate you cleanly, connect the apps that fit your operation, and pair real-time Xero data with quarterly strategic reviews — so your numbers drive decisions, not just compliance.",
    ],
    faqs: [
      {
        question: "Can you provide Xero management accounts and forecasting for my Solihull business?",
        answer:
          "Yes. We build real-time management accounts and cash flow forecasts in Xero for Solihull businesses, then talk you through what they mean in regular reviews.",
      },
      {
        question: "Do you support businesses around the NEC and business parks?",
        answer:
          "Absolutely — many of our Solihull clients are based around Birmingham Business Park, Blythe Valley and the NEC. We support you through Xero and video calls, with in-person meetings when helpful.",
      },
    ],
  },
  {
    slug: "xero-accountants-cannock",
    baseSlug: "accountants-cannock",
    metaDescription:
      "Xero accountants in Cannock. Xero plus connected apps for logistics, warehousing and construction — job management, driver expense capture, fuel cards and CIS.",
    intro: [
      "Cannock's economy runs on movement: distribution and warehousing along the M6, M6 Toll and A5, hauliers, and the construction and groundworks firms working across Staffordshire. For businesses like these, Xero on its own is rarely the whole answer — it is Xero plus the right connected apps.",
      "For logistics and haulage, the pressure points are volume and expenses on the road. We build bank rules around fuel cards, tolls, pallet-network charges and handling fees so the routine lines code themselves, and we put mobile receipt capture in drivers' hands so paperwork does not arrive three months later in a carrier bag from a cab.",
      "For construction and groundworks, the useful integration is job management. Xero connected to a job or site management app means hours booked on site, materials ordered and plant hired all reach the accounts against the right job — so you can see contract margin while the contract is still running instead of finding out at year end.",
      "CIS gets set up inside Xero from the outset, with verification, deduction rates and the monthly return running as part of the bookkeeping. We are fourteen miles away along the A5 near Tamworth, so a site visit is straightforward when it is worth doing.",
    ],
    faqs: [
      {
        question: "Can Xero cope with a haulage business's transaction volumes?",
        answer:
          "Yes, once it is configured for it. Fuel cards, tolls, pallet-network invoices and driver expenses generate a lot of lines, so we build bank rules around your recurring costs and let the routine items code themselves. Only genuine exceptions need looking at.",
      },
      {
        question: "How do drivers and site staff get receipts to us?",
        answer:
          "Through the app on their phone. They photograph the fuel receipt or delivery note and it flows into Xero coded and ready — which is a great deal more reliable than an envelope arriving from a cab or a site office weeks after the event.",
      },
      {
        question: "Can Xero connect to a job or site management system?",
        answer:
          "Usually, yes. There are well-established integrations for the main construction and job management apps, and connecting one is normally what turns Xero from a bookkeeping system into something that tells you whether a contract is making money.",
      },
      {
        question: "Does Xero handle CIS for subcontractors?",
        answer:
          "It does. We switch it on and set up verification, the correct deduction rates and the monthly CIS return, so subcontractor deductions are handled as part of the routine rather than reconstructed each month.",
      },
    ],
  },
  {
    slug: "xero-accountants-burton-upon-trent",
    baseSlug: "accountants-burton-upon-trent",
    metaDescription:
      "Xero accountants in Burton-upon-Trent. Xero Platinum Partner offering setup, migration, training and cloud accounting for manufacturing, brewing and logistics firms.",
    intro: [
      "Burton-upon-Trent's manufacturing, brewing and logistics businesses value precision — and that's exactly what cloud accounting delivers. As a Xero Platinum Partner, we set Xero up to give you accurate, real-time numbers and true visibility of your margins.",
      "We migrate you across without disruption, tie in stock and job costing where it matters, and provide the cash flow forecasting that helps Burton businesses plan ahead with confidence.",
    ],
    faqs: [
      {
        question: "Can Xero handle stock and job costing for my Burton business?",
        answer:
          "Yes. We configure Xero (with the right add-ons) to handle stock and job costing for Burton-upon-Trent manufacturing and logistics businesses, so your margins are always clear.",
      },
      {
        question: "Will moving to Xero disrupt my business?",
        answer:
          "No. We plan the migration carefully, move your data cleanly and train your team, so the switch to Xero is smooth with no disruption to trading.",
      },
    ],
  },
  {
    slug: "xero-accountants-nuneaton",
    baseSlug: "accountants-nuneaton",
    metaDescription:
      "Xero accountants in Nuneaton. Job costing, supplier invoice automation and MRP integrations for automotive supply chain, engineering and distribution businesses.",
    intro: [
      "Nuneaton's business base leans heavily towards engineering, automotive supply and distribution — the Bermuda Park estates, the units off the A444, and the cluster of firms serving the automotive engineering work that goes on nearby. Xero for a tier-two automotive supplier is a different proposition to Xero for a shop.",
      "The first thing that usually needs fixing is supplier paperwork. An engineering business can receive several hundred purchase invoices a month, and if they arrive by email and get keyed in by hand at month end, the numbers are always three weeks behind. We put Hubdoc or a similar capture tool in front of Xero so invoices are extracted, coded and queued for approval as they arrive.",
      "The second is job and contract costing. If you are quoting on production runs or tooling, you need to see cost against quote per job. We configure Xero Projects, or connect your existing production system where you already run one, so the shop-floor reality reaches the accounts rather than being reconciled to them afterwards.",
      "Where a manufacturing or MRP system is already in place, we do not try to replace it. Xero works best as the financial layer underneath, taking a clean feed of sales and purchases. We are twelve miles up the A5 near Tamworth, so we can be with you when it genuinely helps.",
    ],
    faqs: [
      {
        question: "We get hundreds of supplier invoices a month. Can Xero handle that?",
        answer:
          "Yes, but not by typing them in. We set up automated capture in front of Xero so invoices are read, coded to the right supplier and account, and routed for approval. For most Nuneaton engineering businesses this is the change that gets the accounts genuinely up to date for the first time.",
      },
      {
        question: "Can Xero connect to our production or MRP system?",
        answer:
          "Usually, yes. There are established integrations for the main systems, and where there is not one we can look at a scheduled data feed. We would rather connect what already works on the shop floor than ask you to abandon it.",
      },
      {
        question: "Can we see profit by job or production run?",
        answer:
          "That is the point of setting it up properly. With Xero Projects configured around how you actually quote, you get cost against quote per job — including labour, materials and bought-in parts — while there is still time to act on it.",
      },
      {
        question: "Do we need to be VAT registered to use Xero?",
        answer:
          "No. Xero suits businesses either side of the VAT threshold, and if you are approaching it we would rather get you onto proper digital records before you cross it than scramble afterwards.",
      },
    ],
  },
  {
    slug: "xero-accountants-hinckley",
    baseSlug: "accountants-hinckley",
    metaDescription:
      "Xero accountants in Hinckley. Sage 50 migrations, stock and inventory set-up and job costing for M69 and A5 corridor distribution, engineering and textile businesses.",
    intro: [
      "Hinckley sits where the M69 meets the A5, and the local business base reflects it: distribution and warehousing along the corridor, engineering and manufacturing firms, and the textile and hosiery businesses the town has been known for far longer than either road has existed.",
      "The single most common Xero job we do for Hinckley businesses is getting them off Sage 50. A lot of established manufacturers here have run desktop Sage for fifteen or twenty years, and the worry is always the same — that the history will be lost or the opening balances will not tie up. We move the data across, reconcile the balances to the penny and run both in parallel until you are confident.",
      "Stock is the other thing that needs thought. Xero's own inventory handles straightforward stock well, but if you hold hundreds of lines or manufacture from components you will be better served by a connected app feeding Xero rather than forcing Xero to do a job it was not designed for. We will tell you honestly which side of that line you are on before you spend anything.",
      "And for engineering firms quoting on skilled work, we set up job-level costing so you can see margin per contract rather than only per year. We are about eighteen miles away near Tamworth — close enough to visit, and set up so we never need to.",
    ],
    faqs: [
      {
        question: "We have run Sage 50 for twenty years. Can you move us to Xero without losing anything?",
        answer:
          "Yes, and it is the migration we do most often for Hinckley manufacturers. We bring across your trial balance, customers, suppliers and transactional history, reconcile every opening balance, and keep Sage available read-only afterwards. Nothing is deleted and nothing is guessed at.",
      },
      {
        question: "Can Xero handle our stock?",
        answer:
          "It depends how much you hold and whether you manufacture. Xero's built-in inventory is fine for straightforward stocked goods. If you have a large number of lines, batch tracking or assemblies, a connected inventory app feeding Xero is the better answer — and we will say so rather than sell you a set-up that will not hold up.",
      },
      {
        question: "Can we cost jobs individually?",
        answer:
          "Yes, through Xero Projects. For Hinckley engineering and fabrication businesses this is usually where the real insight is: quoted price against actual labour, materials and bought-in parts, visible while the job is still open.",
      },
      {
        question: "Do you provide Xero training for our team?",
        answer:
          "We do, remotely or in person. Training the people who use it daily — usually whoever raises the invoices and pays the suppliers — makes far more difference than training the owner alone.",
      },
    ],
  },
  {
    slug: "xero-accountants-atherstone",
    baseSlug: "accountants-atherstone",
    metaDescription:
      "Xero accountants in Atherstone. Xero Platinum Partner offering setup, migration, training and cloud accounting for sole traders and family businesses on the A5.",
    intro: [
      "Right along the A5, Atherstone's independent traders and family businesses deserve accounting that's simple and modern. As a Xero Platinum Partner, we get you set up on Xero and take the hassle out of the day-to-day.",
      "We move you across cleanly, show you how to use it, and keep everything accurate and up to date — with a local team just up the road whenever you need us.",
    ],
    faqs: [
      {
        question: "Is Xero suitable for a small Atherstone business or sole trader?",
        answer:
          "Yes. Xero is ideal for Atherstone sole traders and small family businesses — we set it up simply and show you exactly how to use it day to day.",
      },
      {
        question: "Can you help if I've never used cloud accounting before?",
        answer:
          "Of course. We set everything up for you and provide friendly training, so even if you've never used cloud accounting, you'll be confident with Xero quickly.",
      },
    ],
  },
  {
    slug: "xero-accountants-coventry",
    baseSlug: "accountants-coventry",
    metaDescription:
      "Xero accountants for Coventry businesses — automotive, engineering, tech and services. Boutique Xero Platinum Partner offering setup, migration, training and cloud accounting.",
    intro: [
      "Coventry's automotive, engineering and technology businesses move fast, and cloud accounting keeps pace. As a Xero Platinum Partner we set Xero up to give founders and directors real-time margins, forecasting and the numbers behind confident investment decisions.",
      "We migrate you across without disruption, connect the apps your operation needs, and act as the outsourced finance director many Coventry businesses want — all built on live Xero data.",
    ],
    faqs: [
      {
        question: "Can Xero support a growing Coventry tech or engineering business?",
        answer:
          "Yes. We configure Xero for Coventry's automotive, engineering and tech firms — handling margins, forecasting and investment planning as you scale.",
      },
      {
        question: "Can you act as an outsourced finance director using Xero?",
        answer:
          "That's exactly how many Coventry clients use us. With live Xero data we provide the strategic input of a finance director without the full-time cost.",
      },
    ],
  },
  {
    slug: "xero-accountants-walsall",
    baseSlug: "accountants-walsall",
    metaDescription:
      "Xero accountants in Walsall. Desktop Sage and Pegasus migrations, works-order job costing and stock set-up for the borough's metalworking, leather and engineering SMEs.",
    intro: [
      "Walsall has been a manufacturing town for a very long time, and it shows in the businesses we work with — metalworking, locks and fabrication, the leather trade the town is known for, and the engineering SMEs across Darlaston, Aldridge and Bescot. These are often long-established, family-run companies, and that has a direct bearing on how a move to Xero should be handled.",
      "In practice it usually means migrating off desktop software that has been in place for decades — Sage 50, sometimes Pegasus or something bespoke that a relative wrote. The fear is always the same: twenty-five years of history disappearing. It does not have to. We bring the balances and history across, reconcile everything, keep the old system available read-only, and run in parallel until nobody is nervous any more.",
      "The second piece is works-order costing. If you are making to order, the accounts need to tell you what each order cost — material, labour, finishing, carriage — not just what the year looked like. We set that up in Xero so you can see which jobs and which customers are genuinely worth having.",
      "Stock and work in progress get the same treatment. Where Xero's own inventory is enough we use it; where you need batch tracking or assemblies we connect an app built for it rather than bending Xero out of shape. We are around fourteen miles away near Tamworth.",
    ],
    faqs: [
      {
        question: "We have decades of history in Sage. Will we lose it moving to Xero?",
        answer:
          "No. We migrate your trial balance, ledgers and transactional history, reconcile every opening balance before you rely on it, and keep the old system accessible read-only. For most Walsall manufacturers we run both in parallel for a period so the change never feels like a leap.",
      },
      {
        question: "Can Xero cost individual works orders?",
        answer:
          "Yes, set up properly. We configure it around how you actually quote and make, so material, labour, finishing and carriage land against each order. It is usually the first time an owner can see clearly which customers are profitable and which are just busy.",
      },
      {
        question: "Does Xero handle work in progress and stock?",
        answer:
          "Its built-in inventory covers straightforward stock. If you hold many lines, track batches or assemble from components, a connected inventory app feeding Xero is the right answer — and we will tell you which you need before you spend money on either.",
      },
      {
        question: "Our bookkeeper does the day-to-day. Can they keep doing it?",
        answer:
          "Absolutely, and we would encourage it. We train them on Xero, agree who does what, and review the work rather than take it away. Plenty of Walsall businesses keep their long-standing bookkeeper and use us for the reporting, VAT and year end.",
      },
    ],
  },
  {
    slug: "xero-accountants-coleshill",
    baseSlug: "accountants-coleshill",
    metaDescription:
      "Xero accountants in Coleshill near the M6/M42 & Birmingham Airport — logistics, transport and professional firms. Xero Platinum Partner: setup, migration and cloud accounting.",
    intro: [
      "Right by the M6, M42 and Birmingham Airport, Coleshill's logistics operators and professional firms need finances that keep up with a busy, well-connected location. As a Xero Platinum Partner we set Xero up to give you real-time numbers wherever you're working.",
      "We migrate you cleanly, automate the day-to-day admin, and keep your VAT and cash flow crystal clear — with a local team a short drive away.",
    ],
    faqs: [
      {
        question: "Is Xero good for a Coleshill logistics or transport business?",
        answer:
          "Very. We set Xero up for Coleshill logistics and transport firms with the right add-ons for invoicing, expenses and job tracking, giving you real-time cash flow.",
      },
      {
        question: "Can you support us both on site and remotely?",
        answer:
          "Yes. Being close by, we can meet in Coleshill in person and also support you through Xero and video calls — whichever suits you.",
      },
    ],
  },
  {
    slug: "xero-accountants-swadlincote",
    baseSlug: "accountants-swadlincote",
    metaDescription:
      "Xero accountants in Swadlincote — manufacturing, retail and trades in South Derbyshire. Xero Platinum Partner offering setup, migration, training and cloud accounting.",
    intro: [
      "Swadlincote's manufacturers, retailers and trades deserve accounting that's simple, modern and reliable. As a Xero Platinum Partner we move your South Derbyshire business into the cloud and take the hassle out of the day-to-day.",
      "We handle the migration, show you how to use Xero, and keep your VAT and cash flow accurate and up to date — with proactive support all year round.",
    ],
    faqs: [
      {
        question: "Is Xero suitable for a small Swadlincote business?",
        answer:
          "Yes. Xero is ideal for Swadlincote's independent businesses, manufacturers and trades — we set it up simply and train you so it's easy to use day to day.",
      },
      {
        question: "Can you keep my cash flow clear in Xero?",
        answer:
          "Definitely. We set Xero up so your Swadlincote business has real-time cash flow visibility, helping you plan ahead with confidence.",
      },
    ],
  },
  {
    slug: "xero-accountants-rugeley",
    baseSlug: "accountants-rugeley",
    metaDescription:
      "Xero accountants in Rugeley near Cannock Chase — trades, retail and logistics. Xero Platinum Partner offering setup, migration, training and cloud accounting support.",
    intro: [
      "Rugeley's trades, retail and logistics businesses get more from their numbers when accounting is real-time. As a Xero Platinum Partner we get your bookkeeping, VAT and payroll running smoothly in the cloud.",
      "We handle the switch to Xero, automate the repetitive admin and give you clear, usable numbers — with proactive advice whenever a decision comes up.",
    ],
    faqs: [
      {
        question: "Can you migrate my Rugeley business to Xero?",
        answer:
          "Yes. As a Xero Platinum Partner we move your Rugeley business onto Xero cleanly — data across, balances reconciled and your team trained before go-live.",
      },
      {
        question: "Is Xero support included in your fees?",
        answer:
          "Yes. For Rugeley clients on fixed monthly fees, Xero support and advice are included — call whenever you need us, with no clock running.",
      },
    ],
  },
  {
    slug: "xero-accountants-ashby-de-la-zouch",
    baseSlug: "accountants-ashby-de-la-zouch",
    metaDescription:
      "Xero accountants in Ashby-de-la-Zouch — professional services, logistics and retail. Xero Platinum Partner offering setup, migration, training and cloud accounting.",
    intro: [
      "Ashby-de-la-Zouch's professional firms, retailers and logistics businesses want to move beyond once-a-year accounting. As a Xero Platinum Partner we set Xero up to give you real-time management information and forecasting.",
      "We migrate you cleanly, connect the right apps, and pair live Xero data with regular reviews — so your numbers help you decide, not just comply.",
    ],
    faqs: [
      {
        question: "Can Xero give me management accounts for my Ashby business?",
        answer:
          "Yes. We build real-time management accounts and cash flow forecasts in Xero for Ashby-de-la-Zouch businesses, and talk you through what they mean.",
      },
      {
        question: "Do you migrate from other software to Xero?",
        answer:
          "We do — regularly moving Ashby businesses from Sage, QuickBooks and spreadsheets onto Xero, handling the data and training throughout.",
      },
    ],
  },
  {
    slug: "xero-accountants-burntwood",
    baseSlug: "accountants-burntwood",
    metaDescription:
      "Xero accountants in Burntwood. Xero set-up for consultants, contractors and home-based sole traders — mobile-first, and ready for Making Tax Digital for Income Tax.",
    intro: [
      "Burntwood is one of the largest towns in the country without a railway station, and that has quietly shaped its business base. A great many of the people we work with here are running their business from home: consultants, contractors, therapists, trades working from a domestic base, and sole traders across Chasetown, Chase Terrace and Boney Hay.",
      "For a one-person business, the value of Xero is not the reporting. It is that the admin stops being a chore. Bank feed connected so nothing is typed in twice, invoices raised from the phone between appointments, receipts photographed and gone, and no January spent reconstructing a year from bank statements and memory.",
      "There is a genuinely important reason to sort this out now. Making Tax Digital for Income Tax is live for sole traders and landlords with qualifying income over fifty thousand pounds, and the threshold steps down after that — which means quarterly digital submissions rather than one annual return. Anyone still working from a spreadsheet has a deadline whether they have noticed it or not. Getting onto Xero properly now turns that from a problem into a non-event.",
      "We are about nine miles away in Amington, Tamworth — a short drive down the A5190. Close enough to sit down over a coffee and go through your first quarter, which for a sole trader is usually worth more than any amount of documentation.",
    ],
    faqs: [
      {
        question: "I am a sole trader working from home. Do I really need Xero?",
        answer:
          "You do not have to have Xero specifically, but you will need digital records. Making Tax Digital for Income Tax is already live for sole traders and landlords above the fifty thousand pound threshold, with lower thresholds following, so quarterly digital submissions are coming for most self-employed people. Xero is simply the least painful way we have found to meet it.",
      },
      {
        question: "What does Making Tax Digital for Income Tax mean for me?",
        answer:
          "Instead of one Self Assessment return after the year end, you keep digital records and submit updates quarterly, then finalise at the end of the year. It is more frequent but much less dramatic, because nothing is left to reconstruct. We handle the submissions and tell you what is due well before it is.",
      },
      {
        question: "Can I use Xero entirely from my phone?",
        answer:
          "Yes, and most Burntwood sole traders we work with do. Raise and send invoices, photograph receipts, check what you are owed and see your bank position — all from the app. The desktop version is there when you want a proper look.",
      },
      {
        question: "Are you local to Burntwood?",
        answer:
          "Yes, genuinely — we are in Amington, Tamworth, about nine miles away down the A5190. We are happy to meet in person, which for a sole trader getting started is usually the fastest way to get comfortable.",
      },
    ],
  },
  {
    slug: "xero-accountants-bedworth",
    baseSlug: "accountants-bedworth",
    metaDescription:
      "Xero accountants in Bedworth. Getting trades and independent businesses off paper and spreadsheets — invoicing from the van, bank rules and CIS handled in Xero.",
    intro: [
      "Bedworth's business community is built largely on trades, small independents and the light industrial units around Bayton Road and Exhall. Most of the businesses we help here are not migrating from another accounting system — they are coming off paper, spreadsheets and a carrier bag of receipts.",
      "That makes the job a different one. There is no data migration to worry about; the work is setting Xero up so simply that it actually gets used. Bank feed connected, a short list of account codes that make sense for your trade rather than a generic chart of accounts, and rules that code your regular suppliers automatically so there is nothing to think about.",
      "The part that changes things for most Bedworth trades businesses is invoicing from the phone. Raise the invoice at the end of the job, in the van, before you have driven home — and it is sent, recorded and chasing itself. Getting paid faster has more effect on a small trade business than almost anything else we could do for it.",
      "If you take on subcontractors we set CIS up inside Xero from the start, so deductions are calculated and the monthly return is a review rather than a rebuild. We are fifteen miles away near Tamworth, and everything runs in the cloud — but we are close enough to sit down with you over the first invoices if that is easier.",
    ],
    faqs: [
      {
        question: "We still do everything on paper. Is Xero going to be too complicated?",
        answer:
          "Not if it is set up for how you actually work. We keep the account codes short and relevant to your trade, connect the bank feed, and set rules so your regular suppliers code themselves. Most Bedworth trades businesses we set up are raising invoices confidently within a week.",
      },
      {
        question: "Can I invoice from my phone on site?",
        answer:
          "Yes, and for a trade business it is the single most useful thing about Xero. Raise it at the end of the job, send it there and then, and let Xero chase it if it goes unpaid. It usually shortens the wait for money noticeably.",
      },
      {
        question: "We use subcontractors. Does Xero handle CIS?",
        answer:
          "It does, once it is switched on and configured. We set up the deduction rates, verification and monthly return so it runs as part of the bookkeeping rather than as a separate job someone has to remember.",
      },
      {
        question: "What does it cost to get started?",
        answer:
          "There is the Xero subscription and our fee for the set-up and ongoing work, both published on our pricing page — build your package and you will see the fixed monthly figure and any one-off costs in about two minutes, before giving us anything. No hourly billing and no surprise invoices.",
      },
    ],
  },
  {
    slug: "xero-accountants-coalville",
    baseSlug: "accountants-coalville",
    metaDescription:
      "Xero accountants in Coalville near the M1 — logistics, distribution, manufacturing and trades. Xero Platinum Partner offering setup, migration and cloud accounting.",
    intro: [
      "Coalville sits by the M1 at the heart of a busy distribution belt, and logistics moves fast. As a Xero Platinum Partner we set Xero up so your business always works from real-time, accurate numbers — never year-old accounts.",
      "We migrate you cleanly, connect the apps you need, and keep tight control of your margins and cash flow with live Xero data.",
    ],
    faqs: [
      {
        question: "Is Xero good for a Coalville logistics or distribution business?",
        answer:
          "Very. We set Xero up for Coalville logistics, warehousing and distribution firms with the right add-ons, giving you real-time cash flow and margins.",
      },
      {
        question: "Can I get real-time numbers rather than year-old accounts?",
        answer:
          "Absolutely. With Xero, your Coalville business has up-to-date figures whenever you need them, not accounts months after year-end.",
      },
    ],
  },
  {
    slug: "xero-accountants-brownhills",
    baseSlug: "accountants-brownhills",
    metaDescription:
      "Xero accountants in Brownhills. Straightforward Xero set-up, bank rules and CIS for the A5 trade counters, canal-side independents and small businesses of the Walsall borough.",
    intro: [
      "Brownhills sits on the A5 with Chasewater on its doorstep, inside the Walsall borough but very much its own place. The businesses here are mostly small and owner-run — trade counters and light industrial units along the A5, independent retailers and services on the High Street, and a good number of one-van and two-van trades.",
      "For businesses this size the honest answer is that Xero should be simple. We see plenty of small companies handed an over-engineered set-up with a hundred and fifty account codes they will never use, and they quietly stop using it. We do the opposite: a short chart of accounts that matches how you talk about your own business, the bank feed connected, and rules doing the repetitive coding.",
      "There is a practical local point worth knowing. Brownhills is administratively part of Walsall Council even though it sits right on the Staffordshire border, so business rates, licensing and waste all go through Walsall rather than Lichfield or Cannock Chase. It catches new businesses out regularly, and we will point you the right way rather than let you find out the hard way.",
      "If you use subcontractors we set CIS up in Xero from day one. We are about eleven miles away near Tamworth, straight along the A5 — genuinely close, and we will not pretend to be closer than that.",
    ],
    faqs: [
      {
        question: "Is Xero overkill for a small business like ours?",
        answer:
          "Not if it is kept simple. The mistake is a set-up built for a company ten times your size. We keep the account codes short and recognisable, connect the bank, and let rules handle the repetitive coding — so it takes minutes a week rather than becoming another job you resent.",
      },
      {
        question: "Which council do we deal with in Brownhills?",
        answer:
          "Walsall Council, for rates, licensing and waste, even though Brownhills sits right on the Staffordshire boundary. It surprises a lot of new businesses who assume Lichfield or Cannock Chase, so it is worth checking before you assume.",
      },
      {
        question: "Can you set up CIS in Xero for us?",
        answer:
          "Yes. We switch it on and configure the deduction rates, subcontractor verification and monthly return so it becomes part of the routine rather than a separate scramble every month.",
      },
      {
        question: "Can we do our own bookkeeping and just use you for the rest?",
        answer:
          "Of course. Plenty of Brownhills businesses keep the day-to-day in Xero themselves and use us for VAT, year end and the odd question. We train you properly first, then review rather than take over.",
      },
    ],
  },
  {
    slug: "xero-accountants-west-bromwich",
    baseSlug: "accountants-west-bromwich",
    metaDescription:
      "Xero accountants in West Bromwich. Moving Black Country manufacturing and trades businesses off paper and desktop software, with job costing and stock set up properly.",
    intro: [
      "West Bromwich is Black Country manufacturing country, and the businesses we support in the Sandwell area reflect that: metalworking and fabrication, engineering subcontractors, plant and vehicle trades, and the distribution firms that serve them. Many are second or third generation family companies.",
      "What that means in practice is that we are often not replacing a modern system — we are replacing a mixture of paper, a desktop package nobody has updated in years, and a spreadsheet that only one person understands. The risk in that situation is not the software, it is the knowledge sitting in one person's head. Getting it into Xero, where it is visible and backed up, is worth as much as any reporting improvement.",
      "Job costing is usually the first thing that pays for itself. Subcontract engineering lives or dies on whether a job was quoted right, and most owners have a strong instinct about which work is profitable without ever having seen it proved. We set Xero up so material, labour and bought-in costs land against each job, and the instinct gets tested.",
      "We also get stock and work in progress onto a proper footing, using Xero's inventory where it is sufficient and a connected app where it is not. We are around seventeen miles away near Tamworth, and we will come over when there is a reason to rather than as a formality.",
    ],
    faqs: [
      {
        question: "Everything is on paper and in one person's spreadsheet. Where do we start?",
        answer:
          "With getting it somewhere visible and safe. We build the chart of accounts around how your business actually works, load your opening position, connect the bank feed, and get the routine coding automated. The biggest single gain is usually that the business stops depending on one person's memory.",
      },
      {
        question: "Can Xero tell us if a job was quoted correctly?",
        answer:
          "Yes, and for West Bromwich subcontract engineering it is normally the most valuable part of the exercise. With costs coded to each job you get quoted price against actual cost, which either confirms your instinct about profitable work or quietly corrects it.",
      },
      {
        question: "Do we have to give up our desktop software straight away?",
        answer:
          "No. We keep it available read-only and, where it helps, run both in parallel for a period. Nobody should be asked to trust a new system before the balances have been reconciled and checked.",
      },
      {
        question: "Can you train the person who does our invoicing?",
        answer:
          "Yes, and that is who we would rather train. Whoever raises the invoices and pays the suppliers gets the most out of the training — far more than training the owner alone and hoping it filters down.",
      },
    ],
  },
  {
    slug: "xero-accountants-wolverhampton",
    baseSlug: "accountants-wolverhampton",
    metaDescription:
      "Xero accountants in Wolverhampton. Multi-entity consolidation, approval workflows and project accounting in Xero for engineering, aerospace supply chain and professional firms.",
    intro: [
      "Wolverhampton is a city rather than a town, and the businesses we support there tend to be correspondingly more structured: engineering and aerospace supply chain firms around the i54 corridor, established manufacturers in Bilston and Wednesfield, and professional practices in and around the city centre.",
      "That changes what Xero has to do. More of our Wolverhampton clients run more than one company — a trading company and a property company, or several entities under a holding company — and the question is never just bookkeeping, it is consolidated reporting. We set Xero up per entity with a consistent chart of accounts across all of them, so consolidation is a report rather than a spreadsheet exercise every month.",
      "Approvals matter more at this size too. Where several people can commit spend, purchase orders and invoice approval workflows in Xero give you a proper audit trail and stop the finance function becoming a bottleneck. It is the sort of control that is very hard to retrofit once bad habits have set in.",
      "For aerospace and automotive supply chain work we set up project accounting so contract margin is visible while contracts are still running, and for professional firms we focus on work in progress and recoverability. We are about twenty-four miles away near Tamworth — a straightforward run when a face-to-face genuinely adds something.",
    ],
    faqs: [
      {
        question: "We run several companies. Can Xero consolidate them?",
        answer:
          "Yes. Each company needs its own Xero organisation, but with a consistent chart of accounts across all of them we can produce consolidated management reporting — group profitability, intercompany positions and cash across the whole structure — as a report rather than a monthly spreadsheet rebuild.",
      },
      {
        question: "Can we control who approves spend?",
        answer:
          "You can. We set up purchase orders and multi-level invoice approval in Xero with permissions matched to your structure, so spend is authorised before it is committed and there is a clean audit trail behind every payment.",
      },
      {
        question: "Can Xero show margin on long-running contracts?",
        answer:
          "That is what project accounting is for. We configure it so labour, materials, subcontractors and bought-in costs are attributed to each contract, giving you margin as the contract progresses rather than a verdict once it has finished.",
      },
      {
        question: "We are a professional practice, not a manufacturer. Is Xero right for us?",
        answer:
          "Very much so. For practices the emphasis shifts to work in progress, recoverability and utilisation rather than stock. We set the reporting up around chargeable time and realisation, which is a different build to a manufacturing set-up.",
      },
    ],
  },
  {
    slug: "xero-accountants-leicester",
    baseSlug: "accountants-leicester",
    metaDescription:
      "Xero accountants for Leicester businesses — manufacturing, textiles, distribution, tech and services. Boutique Xero Platinum Partner: setup, migration and cloud accounting.",
    intro: [
      "Leicester's diverse economy — textiles, manufacturing, distribution, tech and professional services — thrives on ambition, and cloud accounting supports it. As a Xero Platinum Partner we set Xero up to give founders real-time margins and forecasting.",
      "We migrate you cleanly, automate the day-to-day, and pair a dedicated contact with senior expertise so your Leicester business makes sharper, faster decisions.",
    ],
    faqs: [
      {
        question: "Do you work with ambitious Leicester businesses on Xero?",
        answer:
          "Yes — from manufacturers and distributors to tech firms and professional services. We set Xero up with management accounts and forecasting to support confident growth.",
      },
      {
        question: "Will I get a boutique service or a call centre?",
        answer:
          "A boutique, hands-on service. You'll have a dedicated contact who knows your Leicester business and its Xero set-up.",
      },
    ],
  },
  {
    slug: "xero-accountants-castle-donington",
    baseSlug: "accountants-castle-donington",
    metaDescription:
      "Xero accountants in Castle Donington. Multi-currency, high-volume bank feeds and job costing set up for air-freight, logistics and motorsport engineering businesses.",
    intro: [
      "Castle Donington is not a typical market town, and Xero here rarely needs a typical set-up. With East Midlands Airport on one side and Donington Park on the other, the businesses we support are air-freight and logistics operators, motorsport and precision engineering firms, and the suppliers who serve both.",
      "That shapes what we actually do in Xero. Freight and cargo work usually means invoicing in more than one currency, so we set up Xero's multi-currency properly and let it handle the revaluation rather than leaving you to reconcile exchange differences by hand. Air-side operations generate a lot of transactions, so we build bank rules and feeds that code the routine items automatically.",
      "For engineering and motorsport businesses the value sits somewhere else entirely — in job costing. Xero Projects, set up correctly, tells you what a build or a contract actually cost against what you quoted. It is a very different conversation to a year-end profit figure, and for anyone quoting fixed prices on skilled work it is the one that matters.",
      "We are about twenty-five miles away, near Tamworth, and we will say that plainly rather than claim to be on your doorstep. Everything we do runs in the cloud, so distance makes no practical difference — and where a visit genuinely helps, we come to you.",
    ],
    faqs: [
      {
        question: "Can you set up Xero for multi-currency freight invoicing?",
        answer:
          "Yes. We enable multi-currency, set your base and foreign currencies, connect live exchange rates and configure the revaluation so gains and losses land in the right place automatically. It is one of the most common Xero jobs we do for Castle Donington businesses working through East Midlands Airport.",
      },
      {
        question: "We quote fixed prices on engineering jobs. Can Xero track what they really cost?",
        answer:
          "That is exactly what Xero Projects is for. We set it up so labour, materials, subcontractors and bought-in parts are coded to each job, then you can see quoted price against actual cost while the work is still live rather than months later.",
      },
      {
        question: "Our bank feed has hundreds of transactions a month. Is Xero going to cope?",
        answer:
          "Comfortably, once it is configured. The trick is bank rules — we build them around your recurring suppliers, fuel, handling charges and tolls so the routine lines code themselves and only genuine exceptions need a human.",
      },
      {
        question: "Are you local to Castle Donington?",
        answer:
          "No, and we would rather be straight about it. We are based in Amington, Tamworth, roughly twenty-five miles away. We work with Castle Donington businesses entirely through Xero and video call, and travel over when there is a good reason to.",
      },
    ],
  },
  {
    slug: "xero-accountants-market-bosworth",
    baseSlug: "accountants-market-bosworth",
    metaDescription:
      "Xero accountants in Market Bosworth — rural enterprises, tourism, hospitality and professional firms. Xero Platinum Partner: setup, migration, training and cloud accounting.",
    intro: [
      "Market Bosworth's rural enterprises, tourism and hospitality businesses and professional firms work better with real-time, cloud-based numbers. As a Xero Platinum Partner we set you up on Xero and keep the day-to-day effortless.",
      "We handle the migration, train you to use Xero confidently, and keep your VAT and cash flow accurate — with proactive support whenever you need it.",
    ],
    faqs: [
      {
        question: "Is Xero suitable for a rural or hospitality Market Bosworth business?",
        answer:
          "Yes. Xero works well for Market Bosworth's rural enterprises, tourism and hospitality businesses — we set it up simply and train you to use it.",
      },
      {
        question: "Can you handle everything from bookkeeping to tax in Xero?",
        answer:
          "We can. We set Xero up to look after bookkeeping, VAT, payroll and tax for your Market Bosworth business, all in one place.",
      },
    ],
  },
];

export const getXeroLocation = (slug) => XERO_LOCATIONS.find((l) => l.slug === slug);
export const xeroSlugForTown = (baseSlug) => XERO_LOCATIONS.find((l) => l.baseSlug === baseSlug)?.slug || null;
